/**
 * Ce qu'une note de crédit va écrire — décidé avant d'écrire, et testable sans
 * base de données.
 *
 * ## Pourquoi ce module existe
 *
 * L'ancien circuit calculait le montant **dans le navigateur** et la fonction
 * serveur l'écrivait tel quel : `amount: amount`. Aucun contrôle. Trois
 * conséquences mesurées en production le 3 septembre 2026 :
 *
 * - **Le mode « montant personnalisé » créait une note de crédit de 0,00 €.**
 *   L'écran envoie `type: 'custom'`, la fonction ne connaît que `'full'` et
 *   `'partial'` : le montant restait à zéro et l'écriture passait sans erreur.
 *   Il existe exactement une note de crédit à 0,00 € en base.
 * - **Rien n'empêchait de créditer deux fois.** Une facture de 110,00 €
 *   portant déjà 55,00 € de note de crédit pouvait en recevoir 110,00 € de
 *   plus. Deux factures en production portent des notes de crédit supérieures
 *   à leur montant.
 * - **Rien n'empêchait de créditer plus que la facture.**
 *
 * Le montant ne peut donc pas venir du client. Il se **calcule** ici, à partir
 * de la facture et des inscriptions, et le serveur refuse ce qui dépasse.
 *
 * ## La règle qui gouverne tout
 *
 *     reste créditable = montant de la facture − notes de crédit déjà émises
 *
 * Aucun mode ne peut dépasser cette borne. C'est la traduction directe de la
 * consigne : « si une note de crédit a déjà été éditée pour cette facture, il
 * faut l'appliquer au restant dû et pas doubler le montant ».
 *
 * ## Et l'argent déjà reçu
 *
 * Si la famille avait déjà payé, créditer la facture rend son paiement sans
 * objet : il ne peut plus rester affecté à une facture qui ne réclame plus
 * rien. Il repart en **provision**, et la famille décide ensuite — se faire
 * rembourser, ou laisser la provision couvrir la facture suivante.
 *
 * Ce n'est pas une invention de ce module : c'est ce que le modèle
 * d'allocation permet, et l'invariant 2 de la base l'imposerait de toute façon
 * — une facture ne peut pas recevoir plus qu'elle ne réclame.
 */

/** Comment le montant de la note de crédit est déterminé. */
export type CreditNoteMode =
  /** Tout ce que la facture réclame encore. */
  | 'full'
  /** Le montant des inscriptions désignées. */
  | 'per_registration'
  /**
   * Le tarif d'une ou plusieurs inscriptions est corrigé ; la note de crédit
   * porte l'écart. **Le cas le plus fréquent en pratique.**
   */
  | 'tariff_correction'
  /** Un montant libre, saisi par l'administration. */
  | 'custom';

/** Pourquoi cette note de crédit est émise. */
export type CreditNoteReason = 'tariff_correction' | 'cancellation' | 'absence' | 'other';

/** La facture, telle que le calcul a besoin de la connaître. */
export interface InvoiceForCredit {
  id: string;
  invoiceNumber: string;
  /** Le montant BRUT, celui du document. */
  amountCents: number;
  /** Total des notes de crédit déjà émises sur cette facture. */
  creditNotesCents: number;
  /** Total des paiements déjà affectés à cette facture. */
  allocatedCents: number;
  status: string;
}

/** Une inscription de la facture. */
export interface RegistrationForCredit {
  id: string;
  /** De quoi la nommer dans un message : « Les Colibris — Ilyan, 03/08 ». */
  label: string;
  priceType: string;
  /** Le prix facturé de cette inscription. */
  amountCents: number;
  cancellationStatus: string;
  /** Déjà crédité sur cette inscription par des notes antérieures. */
  alreadyCreditedCents: number;
}

/** Le nouveau tarif demandé pour une inscription, en mode requalification. */
export interface TariffChange {
  registrationId: string;
  priceTypeAfter: string;
  amountAfterCents: number;
}

/** Ce qui sera écrit pour une inscription. */
export interface PlannedLine {
  registrationId: string;
  label: string;
  amountCents: number;
  priceTypeBefore: string;
  amountBeforeCents: number;
  priceTypeAfter: string | null;
  amountAfterCents: number | null;
  registrationCancelled: boolean;
}

export interface CreditNotePlan {
  /** Ce que la note de crédit portera. Toujours strictement positif. */
  amountCents: number;
  reason: CreditNoteReason;
  lines: PlannedLine[];
  /** Ce que la facture réclamera après cette note. */
  remainingNetAfterCents: number;
  /**
   * La facture ne réclame plus rien : elle passe `cancelled`.
   *
   * Décision du 3 septembre : une note de crédit qui couvre tout annule la
   * facture. Le document reste, mais il ne réclame plus.
   */
  invoiceBecomesCancelled: boolean;
  /**
   * Ce qui doit repartir en provision de la famille.
   *
   * Non nul quand la famille avait payé plus que ce que la facture réclamera
   * désormais. L'argent existe, il ne peut plus rester sur cette facture, et
   * il n'est pas perdu : il attend la décision de la famille.
   */
  provisionCents: number;
  /** À montrer, sans empêcher. */
  warnings: string[];
}

/** Le plan est refusé : rien ne doit être écrit. */
export interface CreditNoteRefusal {
  refusals: string[];
}

export type CreditNoteOutcome = CreditNotePlan | CreditNoteRefusal;

export function isRefused(o: CreditNoteOutcome): o is CreditNoteRefusal {
  return 'refusals' in o;
}

export interface CreditNoteInput {
  mode: CreditNoteMode;
  reason: CreditNoteReason;
  invoice: InvoiceForCredit;
  /** Toutes les inscriptions de la facture, y compris les annulées. */
  registrations: RegistrationForCredit[];
  /** Les inscriptions désignées par l'administration. */
  selectedRegistrationIds: string[];
  /** Requis en mode `custom`. */
  customAmountCents?: number;
  /** Requis en mode `tariff_correction`. */
  tariffChanges?: TariffChange[];
  /**
   * Annuler les inscriptions désignées.
   *
   * **Aucune valeur par défaut, volontairement.** L'ancien formulaire
   * présentait la case déjà cochée, et « ce qui arrive souvent c'est que
   * l'utilisateur oublie de la décocher ». Une case pré-cochée sur un geste
   * irréversible n'est pas un choix, c'est un piège : l'écran doit poser la
   * question et l'appelant doit y répondre.
   */
  cancelRegistrations: boolean;
}

/**
 * Ce mode pose-t-il la question « annuler ou conserver les inscriptions » ?
 *
 * Une correction de tarif ne l'a pas à poser : l'enfant vient au stage, seul
 * son prix change. La poser quand même obligerait l'administration à répondre
 * à une question sans objet, et une question sans objet finit par être cliquée
 * sans être lue — c'est précisément ainsi qu'on décoche mal une case.
 */
export function requiresCancellationChoice(mode: CreditNoteMode): boolean {
  return mode !== 'tariff_correction';
}

/**
 * Le bouton « Créer la note de crédit » peut-il être actif ?
 *
 * Deux conditions, et la seconde est la raison d'être de cette réécriture :
 * le plan doit être applicable, **et** le choix d'annulation doit avoir été
 * fait quand le mode le pose. `null` n'est pas « non » : c'est « pas encore
 * répondu ». Une case pré-cochée confondait les deux.
 */
export function canSubmit(args: {
  mode: CreditNoteMode;
  outcome: CreditNoteOutcome | null;
  /** `null` tant que l'administration n'a pas répondu. */
  cancelChoice: boolean | null;
  submitting: boolean;
}): boolean {
  if (args.submitting) return false;
  if (!args.outcome || isRefused(args.outcome)) return false;
  if (requiresCancellationChoice(args.mode) && args.cancelChoice === null) return false;
  return true;
}

/** Ce qu'une inscription peut encore recevoir en note de crédit. */
function resteCreditable(r: RegistrationForCredit): number {
  return Math.max(0, r.amountCents - r.alreadyCreditedCents);
}

function estAnnulee(r: RegistrationForCredit): boolean {
  return r.cancellationStatus.startsWith('cancelled_');
}

function euros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

/**
 * Calcule ce qu'une note de crédit écrirait, ou dit pourquoi elle est refusée.
 *
 * Toutes les vérifications sont faites avant de rendre un plan : un plan rendu
 * est un plan applicable. C'est ce qui permet à l'écran de le montrer, et au
 * serveur de le rejouer sans refaire les règles.
 */
export function planCreditNote(input: CreditNoteInput): CreditNoteOutcome {
  const { invoice, registrations, selectedRegistrationIds, mode } = input;
  const refusals: string[] = [];
  const warnings: string[] = [];

  // --- La borne, avant tout ---------------------------------------------
  const resteCreditableFacture = Math.max(0, invoice.amountCents - invoice.creditNotesCents);

  /*
    Une facture ANNULÉE peut être créditée, et c'était un refus jusqu'au
    20 septembre 2026.

    Il confondait deux actes. `status === 'cancelled'` dit qu'on a cessé de
    réclamer cette facture dans l'application ; une note de crédit est la
    **pièce** qui éteint la créance. Annuler un enregistrement n'est pas un
    acte comptable : tant qu'aucune note n'existe, le grand livre de la famille
    porte toujours le débit de la facture — et il a raison.

    Mesuré ce jour-là, une fois toutes les entrées et toutes les sorties
    encodées : quatre familles portaient **1 064,50 €** de factures annulées
    que rien n'avait jamais créditées. L'écran de contrôle les nommait — motif
    `facture_annulee_non_creditee` — et proposait même un bloc « À créditer »,
    pendant que ce refus et son jumeau SQL l'interdisaient.

    Le refus devient donc un AVERTISSEMENT. Ce qui garde encore est juste en
    dessous : quand il ne reste plus rien à créditer, rien ne s'écrit.
  */
  if (invoice.status === 'cancelled') {
    warnings.push(
      `La facture ${invoice.invoiceNumber} est annulée. La note de crédit éteindra `
      + `la créance que le grand livre porte encore à son sujet.`,
    );
  }
  if (resteCreditableFacture === 0) {
    refusals.push(
      invoice.creditNotesCents > 0
        ? `La facture ${invoice.invoiceNumber} est déjà entièrement couverte par ${euros(invoice.creditNotesCents)} de notes de crédit : il n'y a plus rien à créditer.`
        : `La facture ${invoice.invoiceNumber} est à 0,00 € : il n'y a rien à créditer.`,
    );
  }
  if (refusals.length > 0) return { refusals };

  // --- Les inscriptions designees ---------------------------------------
  const parId = new Map(registrations.map((r) => [r.id, r]));
  const inconnues = selectedRegistrationIds.filter((id) => !parId.has(id));
  if (inconnues.length > 0) {
    refusals.push(`${inconnues.length} inscription(s) désignée(s) n'appartiennent pas à cette facture.`);
    return { refusals };
  }

  const designees = selectedRegistrationIds.map((id) => parId.get(id)!);
  const retenues = designees.filter((r) => {
    if (estAnnulee(r)) {
      warnings.push(`${r.label} est déjà annulée : elle est écartée de cette note de crédit.`);
      return false;
    }
    if (resteCreditable(r) === 0) {
      warnings.push(`${r.label} est déjà entièrement créditée : elle est écartée.`);
      return false;
    }
    return true;
  });

  // --- Le montant, selon le mode ----------------------------------------
  let montant = 0;
  const lignes: PlannedLine[] = [];

  const ligneSimple = (r: RegistrationForCredit, part: number): PlannedLine => ({
    registrationId: r.id,
    label: r.label,
    amountCents: part,
    priceTypeBefore: r.priceType,
    amountBeforeCents: r.amountCents,
    priceTypeAfter: null,
    amountAfterCents: null,
    registrationCancelled: input.cancelRegistrations,
  });

  if (mode === 'full') {
    montant = resteCreditableFacture;
    // Les lignes servent la tracabilite, pas le calcul : le montant vient de
    // la facture, et il peut depasser la somme des inscriptions retenues quand
    // certaines sont deja creditees.
    let aRepartir = montant;
    for (const r of retenues) {
      if (aRepartir <= 0) break;
      const part = Math.min(aRepartir, resteCreditable(r));
      lignes.push(ligneSimple(r, part));
      aRepartir -= part;
    }
    if (aRepartir > 0 && retenues.length > 0) {
      warnings.push(
        `${euros(aRepartir)} de la note de crédit ne correspondent à aucune inscription — la facture réclame plus que la somme des inscriptions retenues.`,
      );
    }
  } else if (mode === 'per_registration') {
    if (retenues.length === 0) {
      refusals.push('Aucune inscription retenue : désignez au moins un stage à créditer.');
      return { refusals };
    }
    for (const r of retenues) {
      const part = resteCreditable(r);
      montant += part;
      lignes.push(ligneSimple(r, part));
    }
  } else if (mode === 'tariff_correction') {
    const changements = input.tariffChanges ?? [];
    if (changements.length === 0) {
      refusals.push('Aucun tarif corrigé : indiquez le nouveau tarif d\'au moins une inscription.');
      return { refusals };
    }

    for (const c of changements) {
      const r = parId.get(c.registrationId);
      if (!r) {
        refusals.push(`L'inscription ${c.registrationId} n'appartient pas à cette facture.`);
        continue;
      }
      if (estAnnulee(r)) {
        warnings.push(`${r.label} est annulée : son tarif n'est pas corrigé.`);
        continue;
      }
      // Un nouveau tarif superieur n'est pas une note de credit : ce serait une
      // facture complementaire, qui n'existe pas dans ce circuit. Le dire
      // clairement plutot que d'ecrire un montant negatif.
      if (c.amountAfterCents >= r.amountCents) {
        refusals.push(
          `${r.label} : le nouveau tarif (${euros(c.amountAfterCents)}) n'est pas inférieur au tarif facturé (${euros(r.amountCents)}). Une note de crédit ne peut que réduire.`,
        );
        continue;
      }
      const ecart = r.amountCents - c.amountAfterCents;
      const part = Math.min(ecart, resteCreditable(r));
      if (part < ecart) {
        warnings.push(
          `${r.label} : l'écart de tarif est de ${euros(ecart)} mais ${euros(r.amountCents - resteCreditable(r))} ont déjà été crédités ; ${euros(part)} seulement sont ajoutés.`,
        );
      }
      montant += part;
      lignes.push({
        registrationId: r.id,
        label: r.label,
        amountCents: part,
        priceTypeBefore: r.priceType,
        amountBeforeCents: r.amountCents,
        priceTypeAfter: c.priceTypeAfter,
        amountAfterCents: c.amountAfterCents,
        // Une requalification ne touche pas a la presence de l'enfant : il
        // vient au stage, seul son tarif change.
        registrationCancelled: false,
      });
    }
    if (refusals.length > 0) return { refusals };
  } else {
    // custom
    const demande = input.customAmountCents ?? 0;
    if (demande <= 0) {
      // C'est exactement le defaut qui a produit une note de credit a 0,00 €
      // en production, en silence.
      refusals.push('Le montant personnalisé doit être supérieur à 0,00 €.');
      return { refusals };
    }
    montant = demande;
    let aRepartir = montant;
    for (const r of retenues) {
      if (aRepartir <= 0) break;
      const part = Math.min(aRepartir, resteCreditable(r));
      lignes.push(ligneSimple(r, part));
      aRepartir -= part;
    }
    if (aRepartir > 0) {
      warnings.push(
        retenues.length === 0
          ? `Aucune inscription désignée : les ${euros(montant)} sont crédités sur la facture sans être imputés à un stage.`
          : `${euros(aRepartir)} ne peuvent pas être imputés aux inscriptions désignées : ils sont crédités sur la facture.`,
      );
    }
  }

  // --- Les deux refus qui gardent l'argent ------------------------------
  if (montant <= 0) {
    refusals.push('Le calcul aboutit à 0,00 € : il n\'y a rien à créditer.');
    return { refusals };
  }
  if (montant > resteCreditableFacture) {
    refusals.push(
      invoice.creditNotesCents > 0
        ? `Cette note de crédit porterait ${euros(montant)} alors que la facture ${invoice.invoiceNumber} ne peut plus être créditée que de ${euros(resteCreditableFacture)} (${euros(invoice.amountCents)} moins ${euros(invoice.creditNotesCents)} déjà crédités).`
        : `Cette note de crédit porterait ${euros(montant)} alors que la facture ${invoice.invoiceNumber} ne vaut que ${euros(invoice.amountCents)}.`,
    );
    return { refusals };
  }

  // --- Les consequences -------------------------------------------------
  const netApres = resteCreditableFacture - montant;

  /*
    Ce que la famille a paye en trop, une fois la facture reduite.

    Cas de la consigne : facture entierement creditee et deja payee. Mais la
    formule couvre aussi le cas partiel — une facture de 105,00 EUR payee en
    entier puis creditee de 35,00 EUR laisse 35,00 EUR sans objet — et ce cas
    arrivera precisement avec les corrections de tarif, qui sont le motif le
    plus frequent.
  */
  const provision = Math.max(0, invoice.allocatedCents - netApres);
  if (provision > 0) {
    warnings.push(
      `${euros(provision)} déjà reçus ne peuvent plus rester sur cette facture : ils repartent en provision de la famille, qui pourra demander leur remboursement ou les laisser couvrir la facture suivante.`,
    );
  }

  return {
    amountCents: montant,
    reason: input.reason,
    lines: lignes,
    remainingNetAfterCents: netApres,
    invoiceBecomesCancelled: netApres === 0,
    provisionCents: provision,
    warnings,
  };
}
